import { createContext, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, clearCsrfToken } from "@/lib/api";
import type { AuthUser, LoginRequest } from "@/types";

interface AuthContextValue {
  user: AuthUser | null | undefined;
  isLoading: boolean;
  error: Error | null;
  login: (data: LoginRequest) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    error,
  } = useQuery<AuthUser | null>({
    queryKey: ["auth", "me"],
    queryFn: () => api.auth.me(),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const loginMutation = useMutation<AuthUser, Error, LoginRequest>({
    mutationFn: api.auth.login,
    onSuccess: (data) => {
      queryClient.setQueryData(["auth", "me"], data);
      clearCsrfToken();
    },
  });

  const logoutMutation = useMutation<void, Error, void>({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      queryClient.setQueryData(["auth", "me"], null);
      queryClient.clear();
      clearCsrfToken();
    },
  });

  const login = async (data: LoginRequest) => {
    return await loginMutation.mutateAsync(data);
  };

  const logout = async () => {
    await logoutMutation.mutateAsync();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
